import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Polish from '@uppy/locales/lib/pl_PL'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

// Unlike the other uppy_*_upload.js packs (one widget per page/modal), the
// "new files in case file set" modal renders one independent drag-drop area
// per case_file_set in the case, all present in the DOM at once. Track
// instances in a Map keyed by container element instead of a single
// module-level uppyInstance/trackedDragDropArea pair, and iterate over every
// matching element on init instead of picking just the first. Each entry
// also carries its own isBeingDestroyed flag (see destroyUppyInstance) since
// several instances can be torn down independently of one another.
const uppyInstances = new Map();

function destroyUppyInstance(dragDropArea) {
    const entry = uppyInstances.get(dragDropArea);
    if (entry) {
        // Uppy's destroy() calls cancelAll() -> removeFiles(), which emits
        // 'file-removed' for every file still in its state as part of
        // teardown - indistinguishable, from inside the event handler, from
        // a user clicking Uppy's own remove button. Without this flag,
        // tearing down an instance (e.g. on the very next Turbolinks
        // navigation after a user uploads files and closes this modal, per
        // its own "you can close this form now" instructions) fires the
        // same handler that deletes a user-removed CaseFileSetDocumentFile,
        // permanently deleting the files that had just been uploaded.
        entry.state.isBeingDestroyed = true;
        entry.instance.destroy();
        uppyInstances.delete(dragDropArea);
    }
}

function destroyAllUppyInstances() {
    Array.from(uppyInstances.keys()).forEach(destroyUppyInstance);
}

function initCaseFileSetDocumentFileUppyIfPresent() {
    document.querySelectorAll('.case-file-set-document-file-uppy-drag-drop-area').forEach((dragDropArea) => {
        if (dragDropArea.dataset.uppyInitialized === 'true') return;
        dragDropArea.dataset.uppyInitialized = 'true';

        const containerId = dragDropArea.id;
        const caseFileSetId = dragDropArea.dataset.tagId;
        const state = {isBeingDestroyed: false};

        const uppyInstance = new Uppy({
            autoProceed: true,
            restrictions: {
                maxFileSize: parseInt(dragDropArea.dataset.maxFileSize),
                maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles)
            },
            locale: Polish
        })

        uppyInstance.use(Dashboard, {
            inline: true,
            target: `#${containerId}`,
            showProgressDetails: true,
            proudlyDisplayPoweredByUppy: false,
            height: 150,
            width: '100%',
            showRemoveButtonAfterComplete: true
        })

        uppyInstance.use(AwsS3Multipart, {
            id: 'AwsS3Multipart',

            mode: 'custom',

            shouldUseMultipart: (_file) => true,

            createMultipartUpload: async (file) => {
                const response = await fetch('/s3/multipart', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    },
                    body: JSON.stringify({
                        filename: file.name,
                        type: file.type
                    })
                })
                const result = await response.json()
                return {
                    uploadId: result.uploadId,
                    key: result.key
                }
            },

            signPart: async (file, {uploadId, key, partNumber}) => {
                const res = await fetch(`/s3/multipart/${uploadId}/${partNumber}?key=${encodeURIComponent(key)}`, {
                    method: 'GET'
                })

                const {url} = await res.json()
                return {
                    url,
                    headers: {'Content-Type': 'application/octet-stream'}
                }
            },

            completeMultipartUpload: async (file, {key, uploadId, parts}) => {
                const response = await fetch(`/s3/multipart/${uploadId}/complete?key=${encodeURIComponent(key)}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    },
                    body: JSON.stringify({
                        parts,
                        model: 'CaseFileSetDocumentFile',
                        tagId: caseFileSetId,
                        token: dragDropArea.dataset.token
                    })
                })
                const result = await response.json()

                // The file itself is already fully uploaded to S3 at this point —
                // this request only asks the server to persist the
                // CaseFileSetDocumentFile record (running e.g. extension
                // validation). A 4xx here means that failed, so surface it
                // instead of reporting success with no record ever created.
                if (!response.ok) {
                    throw new Error((result.errors || []).join(', ') || 'Nie udało się zapisać pliku')
                }

                uppyInstance.setFileState(file.id, {modelId: result.document_file_id})

                return {location: result.location}
            },

            abortMultipartUpload: async (file, {key, uploadId}) => {
                await fetch(`/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    }
                })
            }
        })

        // Mirrors the old Dropzone widget's "queuecomplete" handler: refresh
        // this case_file_set's green checkmark + file list on the page behind
        // this modal once the current batch of uploads finishes. The checklist
        // is rendered in both the ground_information and documents_notes tabs,
        // so update every copy, not just the first.
        uppyInstance.on('complete', () => {
            fetch(`/case_file_sets/${caseFileSetId}/show_files`)
                .then((response) => response.text())
                .then((html) => {
                    const parsed = new DOMParser().parseFromString(html, 'text/html');
                    document.querySelectorAll(`.case-file-set-${caseFileSetId}-sign`).forEach((sign) => {
                        sign.innerHTML =
                            '<span class="glyphicon glyphicon-ok-circle" aria-hidden="true" style="color: green; font-size: 20px;"></span>';
                    });
                    document.querySelectorAll(`.case-file-set-${caseFileSetId}-files`).forEach((files) => {
                        files.innerHTML = parsed.body.innerHTML;
                    });
                });
        });

        uppyInstance.on('file-removed', (file) => {
            if (state.isBeingDestroyed) return;

            if (file.modelId) {
                fetch('/case_file_set_document_files/' + file.modelId, {
                    method: 'DELETE',
                    headers: {
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    }
                })
            }
        })

        uppyInstances.set(dragDropArea, {instance: uppyInstance, state});
    });
}

function handleDomMutation() {
    Array.from(uppyInstances.keys()).forEach((dragDropArea) => {
        if (!document.body.contains(dragDropArea)) destroyUppyInstance(dragDropArea);
    });
    initCaseFileSetDocumentFileUppyIfPresent();
}

document.addEventListener('turbolinks:before-render', destroyAllUppyInstances);
document.addEventListener('DOMContentLoaded', initCaseFileSetDocumentFileUppyIfPresent);
document.addEventListener('turbolinks:load', initCaseFileSetDocumentFileUppyIfPresent);

// The pack tag executes before <body> exists (no defer), so document.body
// isn't available yet at top level — start observing once it is.
function startObservingBody() {
    if (!document.body) {
        setTimeout(startObservingBody, 0);
        return;
    }
    new MutationObserver(handleDomMutation).observe(document.body, {childList: true, subtree: true});
    initCaseFileSetDocumentFileUppyIfPresent();
}

startObservingBody();
